import { defineStore } from "pinia";
import { v4 as uuidv4 } from "uuid";

import type { State, Toast } from "./types";

export const TIMEOUT = 5000;

/**
 * Use this store to add toasts to the UI.
 *
 * It needs the ToastViewer component which is a simple component that displays
 * the toasts. This component is added to the highest level of the app, so it
 * can be seen from anywhere.
 *
 * If the ToastViewer is already registered at the topmost level in the app,
 * this one instance is sufficient for the whole app and you don't need to
 * worry about it.
 */
export const useToastsStore = defineStore("toasts", {
  state: (): State => ({
    toasts: {},
  }),

  getters: {
    /*
     * Returns all toasts that have not been shown yet, sorted by timestamp.
     */
    toShow(state: State): Toast[] {
      return Object.values(state.toasts)
        .filter((toast: Toast) => !toast.shown)
        .sort((a, b) => {
          return a.timestamp.getTime() - b.timestamp.getTime();
        });
    },
  },

  actions: {
    /**
     * Add a toast to the UI.
     */
    addToast({ message, type }: { message: Toast["message"]; type: Toast["type"] }): void {
      const toast: Toast = {
        message,
        type,
        timestamp: new Date(),
        shown: false,
        id: uuidv4(),
      };
      this.toasts[toast.id] = toast;
    },

    /**
     * Mark a toast as shown.
     */
    markToastAsShown(id: string): void {
      if (this.toasts[id]) {
        this.toasts[id].shown = true;
      }
      this.cleanupOldToasts();
    },

    /**
     * Remove any toasts older than 5 minutes
     */
    cleanupOldToasts(): void {
      const now = new Date();
      const minutesAgo = 5;
      const millisecondsPerMinute = 60 * 1000; // 1 minute = 60 seconds * 1000 milliseconds
      const cutoff = new Date(now.getTime() - minutesAgo * millisecondsPerMinute);

      const idsToRemove = Object.values(this.toasts)
        .filter((toast) => toast.timestamp < cutoff)
        .map((toast) => toast.id);

      const updatedToasts = Object.fromEntries(
        Object.entries(this.toasts).filter(([id]) => !idsToRemove.includes(id)),
      );
      this.toasts = updatedToasts;
    },
  },
});
